import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  CloudOff,
  ExternalLink,
  History,
  Layers,
  Loader2,
  MousePointerClick,
  Rocket,
  SlidersHorizontal,
  Smartphone,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FLOW_KIND_LABEL,
  FLOW_STATUS_LABEL,
  type FlowDetail,
  type FlowIssueView,
  type FlowSettings,
  type FlowTheme,
} from "@rn/contracts";
import {
  BLOCK_LABEL,
  flowVariables,
  validateFlow,
  type BlockType,
  type FlowGraph,
  type FlowNode,
} from "@rn/core/flow";
import { BlockFields } from "@/components/flows/BlockFields";
import { BlockList } from "@/components/flows/BlockList";
import { BlockLogic, type LogicTarget } from "@/components/flows/BlockLogic";
import {
  BLOCK_ICON,
  blockSummary,
  createBlock,
  newId,
  slugProblem,
} from "@/components/flows/blocks";
import { SectionLabel } from "@/components/flows/fields";
import { FlowPreview } from "@/components/flows/FlowPreview";
import {
  branchEdges,
  duplicateNode,
  graphToList,
  insertNode,
  moveItem,
  removeNode,
  replaceNode,
  scoreRangeOf,
  scoreRouterId,
  setAfter,
  setBranchEdges,
  setScoreRange,
} from "@/components/flows/linear";
import { ReportPanel } from "@/components/flows/ReportPanel";
import { SessionsPanel } from "@/components/flows/SessionsPanel";
import { SettingsFields } from "@/components/flows/SettingsFields";
import { SharePanel } from "@/components/flows/SharePanel";
import { ThemeFields } from "@/components/flows/ThemeFields";
import { VersionsDialog } from "@/components/flows/VersionsDialog";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import {
  asFlowGraph,
  errorMessage,
  publishIssues,
  uploadFlowMedia,
  useFlow,
  usePublishFlow,
  useUpdateFlow,
  type FlowPatch,
} from "@/lib/flows";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/formularios/$flowId")({
  component: EditorPage,
});

const TOM_STATUS: Record<FlowDetail["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

/**
 * Editor de formulário e quiz.
 *
 * Tela de altura fixa: cabeçalho de uma linha, abas, e cada coluna rola por
 * dentro. O rascunho vive em estado local e é salvo sozinho (800 ms depois da
 * última mudança); o cache do React Query recebe a resposta do servidor, que
 * traz o status e o "tem alteração não publicada".
 */
function EditorPage() {
  const { flowId } = Route.useParams();
  const flow = useFlow(flowId);
  // Restaurar uma versão troca o rascunho inteiro: remontar o editor é mais
  // simples (e mais seguro) do que sincronizar cada estado local.
  const [rodada, setRodada] = useState(0);

  if (flow.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
          <Skeleton className="size-8" />
          <Skeleton className="h-6 w-56" />
          <Skeleton className="ml-auto h-9 w-28" />
        </div>
        <div className="grid min-h-0 flex-1 gap-4 p-4 lg:grid-cols-[17rem_1fr_22rem]">
          <Skeleton className="h-full" />
          <Skeleton className="hidden h-full lg:block" />
          <Skeleton className="hidden h-full lg:block" />
        </div>
      </div>
    );
  }

  if (flow.isError || !flow.data) {
    const naoExiste = flow.error instanceof ApiError && flow.error.status === 404;
    return (
      <EmptyState
        icon={AlertCircle}
        title={naoExiste ? "Formulário não encontrado" : "Não foi possível abrir o formulário"}
        description={
          naoExiste
            ? "Ele pode ter sido excluído, ou o link é de outra empresa."
            : "Confira a conexão e tente de novo."
        }
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/formularios">
              <ArrowLeft />
              Voltar para a lista
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <FlowEditor
      key={`${flowId}:${rodada}`}
      initial={flow.data}
      onReload={() => setRodada((r) => r + 1)}
    />
  );
}

type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

/**
 * Salvamento automático com fila.
 *
 * As mudanças se acumulam num patch só e saem 800 ms depois da última. Um
 * salvamento por vez: se a pessoa continua editando enquanto o PATCH está em
 * voo, o que chegou depois vai no seguinte — dois PATCH paralelos podiam
 * chegar fora de ordem e gravar o rascunho velho por cima do novo.
 */
function useAutosave(flowId: string) {
  const { mutateAsync } = useUpdateFlow(flowId);
  const pendente = useRef<FlowPatch>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emVoo = useRef<Promise<void> | null>(null);
  const [estado, setEstado] = useState<SaveState>("idle");
  const [slugEmUso, setSlugEmUso] = useState<string | null>(null);

  const flush = useCallback(async (): Promise<void> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (emVoo.current) await emVoo.current;
    const patch = pendente.current;
    if (Object.keys(patch).length === 0) return;
    pendente.current = {};
    setEstado("saving");

    const rodada = mutateAsync(patch)
      .then(() => {
        if (patch.slug) setSlugEmUso(null);
        setEstado(Object.keys(pendente.current).length > 0 ? "pending" : "saved");
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409 && patch.slug) {
          // Slug repetido: o resto do patch ainda vale; só o endereço volta.
          setSlugEmUso("Esse endereço já está em uso por outro fluxo. Escolha outro.");
          const { slug: _s, ...resto } = patch;
          if (Object.keys(resto).length > 0) pendente.current = { ...resto, ...pendente.current };
          setEstado("error");
          return;
        }
        // Devolve à fila: a próxima mudança (ou o "tentar de novo") manda tudo.
        pendente.current = { ...patch, ...pendente.current };
        setEstado("error");
        toast.error(errorMessage(e, "Não foi possível salvar o rascunho"));
      });
    emVoo.current = rodada;
    await rodada;
    emVoo.current = null;
  }, [mutateAsync]);

  const queue = useCallback(
    (patch: FlowPatch) => {
      pendente.current = { ...pendente.current, ...patch };
      setEstado("pending");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), 800);
    },
    [flush],
  );

  // Saiu da tela com algo na fila: manda na hora em vez de perder.
  useEffect(() => {
    const avisar = (e: BeforeUnloadEvent) => {
      if (Object.keys(pendente.current).length > 0 || emVoo.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", avisar);
    return () => {
      window.removeEventListener("beforeunload", avisar);
      void flush();
    };
  }, [flush]);

  return { estado, queue, flush, slugEmUso };
}

/** Tira do tema o que o servidor recusaria (cor fora do formato). */
function temaValido(theme: FlowTheme): FlowTheme {
  const hex = /^#[0-9a-fA-F]{6}$/;
  const limpo: FlowTheme = { ...theme };
  for (const k of ["primaryColor", "backgroundColor", "textColor"] as const) {
    if (limpo[k] !== undefined && !hex.test(limpo[k]!)) delete limpo[k];
  }
  return limpo;
}

type Painel = "blocos" | "props" | "previa";

function FlowEditor({ initial, onReload }: { initial: FlowDetail; onReload: () => void }) {
  const flowId = initial.id;
  // O cache recebe a resposta de cada PATCH/publicação: status, versão e
  // "alterações não publicadas" vêm daqui; o conteúdo, do estado local.
  const live = useFlow(flowId).data ?? initial;
  const kind = initial.kind;

  const [name, setName] = useState(initial.name);
  const [slug, setSlug] = useState(initial.slug);
  const [graph, setGraph] = useState<FlowGraph>(() => asFlowGraph(initial.draft));
  const [theme, setTheme] = useState<FlowTheme>(initial.theme ?? {});
  const [settings, setSettings] = useState<FlowSettings>(initial.settings ?? {});
  const [selecionado, setSelecionado] = useState<string | null>(
    () => graphToList(asFlowGraph(initial.draft))[0]?.node.id ?? null,
  );
  const [painel, setPainel] = useState<Painel>("blocos");
  const [versoes, setVersoes] = useState(false);

  const { estado, queue, flush, slugEmUso } = useAutosave(flowId);
  const publicar = usePublishFlow(flowId);

  const itens = useMemo(() => graphToList(graph), [graph]);
  const variaveis = useMemo(() => flowVariables(graph), [graph]);
  const issues: FlowIssueView[] = useMemo(() => validateFlow(graph), [graph]);
  const erros = issues.filter((i) => i.level === "error");
  const porBloco = useMemo(() => {
    const m = new Map<string, FlowIssueView[]>();
    for (const i of issues) if (i.nodeId) m.set(i.nodeId, [...(m.get(i.nodeId) ?? []), i]);
    return m;
  }, [issues]);
  const gerais = issues.filter((i) => !i.nodeId);

  // ── Mudanças ──────────────────────────────────────────────────────────────

  const mudarGrafo = (novo: FlowGraph) => {
    setGraph(novo);
    // Grafo vazio não passa no servidor (precisa de início); fica só local
    // até a pessoa pôr o primeiro bloco.
    if (Object.keys(novo.nodes).length > 0) queue({ draft: novo });
  };

  const mudarTema = (t: FlowTheme) => {
    setTheme(t);
    queue({ theme: temaValido(t) });
  };

  const mudarAjustes = (s: FlowSettings) => {
    setSettings(s);
    queue({ settings: s });
  };

  const mudarNome = (n: string) => {
    setName(n);
    if (n.trim()) queue({ name: n.trim() });
  };

  const erroSlug = slugProblem(slug) ?? (slug === live.slug ? null : slugEmUso);
  const mudarSlug = (s: string) => {
    setSlug(s);
    if (!slugProblem(s)) queue({ slug: s });
  };

  const adicionar = (type: BlockType) => {
    const bloco = createBlock(type, { variables: variaveis, kind });
    const i = itens.findIndex((it) => it.node.id === selecionado);
    const onde = i >= 0 ? i + 1 : itens.length;
    // No quiz, cada tela de resultado encerra: senão quem cai no primeiro
    // resultado veria o segundo logo em seguida.
    mudarGrafo(
      insertNode(graph, bloco, onde, type === "result" || type === "end" ? "end" : "next"),
    );
    setSelecionado(bloco.id);
    setPainel("props");
  };

  const remover = (id: string) => {
    const antes = graph;
    const i = itens.findIndex((it) => it.node.id === id);
    mudarGrafo(removeNode(graph, id));
    if (selecionado === id) setSelecionado(itens[i + 1]?.node.id ?? itens[i - 1]?.node.id ?? null);
    toast("Bloco excluído", {
      action: {
        label: "Desfazer",
        onClick: () => {
          mudarGrafo(antes);
          setSelecionado(id);
        },
      },
    });
  };

  const duplicar = (id: string) => {
    const novoId = newId();
    mudarGrafo(duplicateNode(graph, id, novoId));
    setSelecionado(novoId);
  };

  const executarPublicacao = async () => {
    await flush();
    try {
      const f = await publicar.mutateAsync();
      toast.success(`Publicado — versão ${f.publishedVersion ?? ""}`.trim(), {
        description: f.publicUrl,
        action: { label: "Abrir", onClick: () => window.open(f.publicUrl, "_blank", "noopener") },
      });
    } catch (e) {
      const pendencias = publishIssues(e).filter((i) => i.level === "error");
      toast.error(
        pendencias.length > 0
          ? `Não publicou: ${pendencias.map((p) => p.message).join(" · ")}`
          : errorMessage(e, "Não foi possível publicar"),
      );
    }
  };

  // ── Derivados da tela ─────────────────────────────────────────────────────

  const salvando = estado === "pending" || estado === "saving";
  const naoPublicado =
    live.status === "published" && (live.hasUnpublishedChanges || salvando || estado === "error");
  const podePublicar =
    erros.length === 0 &&
    itens.length > 0 &&
    live.status !== "archived" &&
    (live.status !== "published" || naoPublicado);

  const idxSel = itens.findIndex((it) => it.node.id === selecionado);
  const itemSel = idxSel >= 0 ? itens[idxSel]! : null;
  const rotulo = (i: number) => {
    const n = itens[i]!.node;
    const resumo = blockSummary(n);
    return `${i + 1}. ${BLOCK_LABEL[n.type]}${resumo ? `: ${resumo.slice(0, 32)}` : ""}`;
  };
  const destinos: LogicTarget[] = itens
    .map((it, i) => ({ id: it.node.id, label: rotulo(i) }))
    .filter((t) => t.id !== selecionado);

  const mudarBloco = (node: FlowNode) => mudarGrafo(replaceNode(graph, node));
  const temRoteador = kind === "quiz" && scoreRouterId(graph) !== null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Tabs defaultValue="editor" className="flex min-h-0 flex-1 flex-col">
        {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
        <div className="shrink-0 border-b px-4 pt-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Hint label="Voltar para a lista">
              <Button asChild size="icon-sm" variant="ghost" aria-label="Voltar para a lista">
                <Link to="/formularios">
                  <ArrowLeft />
                </Link>
              </Button>
            </Hint>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input
                value={name}
                aria-label="Nome do formulário"
                maxLength={120}
                onChange={(e) => mudarNome(e.target.value)}
                className="min-w-0 flex-1 truncate rounded-md bg-transparent px-1.5 py-1 font-display text-base font-semibold tracking-tight transition-colors hover:bg-muted focus-visible:bg-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 sm:max-w-sm sm:text-lg"
              />
              <Badge variant="soft" className="hidden sm:inline-flex">
                {FLOW_KIND_LABEL[kind]}
              </Badge>
              <Badge variant={TOM_STATUS[live.status]}>
                {FLOW_STATUS_LABEL[live.status]}
                {live.publishedVersion ? ` · v${live.publishedVersion}` : ""}
              </Badge>
              {naoPublicado && (
                <Badge variant="warning" className="hidden md:inline-flex">
                  alterações não publicadas
                </Badge>
              )}
              <EstadoSalvo estado={estado} onRetry={() => void flush()} />
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <Hint label="Versões publicadas">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Versões publicadas"
                  onClick={() => setVersoes(true)}
                >
                  <History />
                </Button>
              </Hint>
              {live.publishedVersion ? (
                <Button asChild size="sm" variant="outline">
                  <a href={live.publicUrl} target="_blank" rel="noreferrer">
                    <ExternalLink />
                    <span className="hidden sm:inline">Ver página</span>
                  </a>
                </Button>
              ) : (
                <Hint label="Publique primeiro para a página existir">
                  <span tabIndex={0} className="inline-flex rounded-md">
                    <Button size="sm" variant="outline" disabled>
                      <ExternalLink />
                      <span className="hidden sm:inline">Ver página</span>
                    </Button>
                  </span>
                </Hint>
              )}
              <Hint
                label={
                  erros.length > 0 ? (
                    <span className="block space-y-0.5">
                      <span className="block font-semibold">Corrija antes de publicar:</span>
                      {erros.slice(0, 6).map((e, i) => (
                        <span key={i} className="block">
                          •{" "}
                          {e.nodeId && porBloco.has(e.nodeId)
                            ? `Bloco ${itens.findIndex((it) => it.node.id === e.nodeId) + 1}: `
                            : ""}
                          {e.message}
                        </span>
                      ))}
                      {erros.length > 6 && (
                        <span className="block">e mais {erros.length - 6}…</span>
                      )}
                    </span>
                  ) : live.status === "archived" ? (
                    "Arquivado: duplique para voltar a usar"
                  ) : !podePublicar ? (
                    "Nada novo para publicar"
                  ) : (
                    "Põe o rascunho no ar como uma versão nova"
                  )
                }
              >
                <span tabIndex={podePublicar ? -1 : 0} className="inline-flex rounded-md">
                  <Button
                    size="sm"
                    disabled={!podePublicar || publicar.isPending}
                    onClick={() => void executarPublicacao()}
                  >
                    {publicar.isPending ? <Loader2 className="animate-spin" /> : <Rocket />}
                    {publicar.isPending ? "Publicando…" : "Publicar"}
                    {erros.length > 0 && (
                      <span className="rounded-full bg-primary-foreground/20 px-1.5 text-[10px] tabular-nums">
                        {erros.length}
                      </span>
                    )}
                  </Button>
                </span>
              </Hint>
            </div>
          </div>

          <TabsList className="mt-2 border-b-0">
            <TabsTrigger value="editor">Editor</TabsTrigger>
            <TabsTrigger value="tema">Tema</TabsTrigger>
            <TabsTrigger value="ajustes">Ajustes</TabsTrigger>
            <TabsTrigger value="compartilhar">Compartilhar</TabsTrigger>
            <TabsTrigger value="respostas">Respostas</TabsTrigger>
            <TabsTrigger value="relatorio">Relatório</TabsTrigger>
          </TabsList>
        </div>

        {/* ── Editor ─────────────────────────────────────────────────────── */}
        <TabsContent value="editor" className="flex min-h-0 flex-1 flex-col pt-0">
          <div className="shrink-0 border-b px-4 py-2 lg:hidden">
            <div className="inline-flex w-full gap-0.5 rounded-lg bg-muted p-0.5" role="tablist">
              {(
                [
                  { v: "blocos", label: "Blocos", icon: Layers },
                  { v: "props", label: "Propriedades", icon: SlidersHorizontal },
                  { v: "previa", label: "Prévia", icon: Smartphone },
                ] as const
              ).map((p) => (
                <button
                  key={p.v}
                  type="button"
                  role="tab"
                  aria-selected={painel === p.v}
                  onClick={() => setPainel(p.v)}
                  className={cn(
                    "inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
                    painel === p.v
                      ? "bg-card text-foreground shadow-card"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <p.icon className="size-3.5" />
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] lg:grid-cols-[17rem_minmax(0,1fr)_minmax(18rem,22rem)]">
            {/* Lista */}
            <aside
              className={cn(
                "min-h-0 overflow-y-auto bg-muted/20 p-3 lg:border-r",
                painel !== "blocos" && "hidden lg:block",
              )}
            >
              {gerais.length > 0 && (
                <div className="mb-2 space-y-1">
                  {gerais.map((g, i) => (
                    <p
                      key={i}
                      className={cn(
                        "rounded-lg px-2.5 py-1.5 text-xs",
                        g.level === "error"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-warning/18 text-warning-foreground dark:text-warning",
                      )}
                    >
                      {g.message}
                    </p>
                  ))}
                </div>
              )}
              <BlockList
                items={itens}
                kind={kind}
                selectedId={selecionado}
                onSelect={(id) => {
                  setSelecionado(id);
                  setPainel("props");
                }}
                onMove={(de, para) => mudarGrafo(moveItem(graph, de, para))}
                onDuplicate={duplicar}
                onRemove={remover}
                onAdd={adicionar}
                issuesByNode={porBloco}
              />
            </aside>

            {/* Propriedades */}
            <section
              className={cn(
                "min-h-0 overflow-y-auto px-4 py-5 sm:px-6",
                painel !== "props" && "hidden lg:block",
              )}
            >
              {itemSel ? (
                <div className="mx-auto max-w-2xl space-y-6">
                  <CabecalhoDoBloco
                    node={itemSel.node}
                    posicao={idxSel + 1}
                    total={itens.length}
                    problemas={porBloco.get(itemSel.node.id) ?? []}
                  />
                  <BlockFields
                    key={itemSel.node.id}
                    node={itemSel.node}
                    onChange={mudarBloco}
                    variables={variaveis}
                    onUploadMedia={uploadFlowMedia}
                    kind={kind}
                    {...(itemSel.node.type === "result" && temRoteador
                      ? {
                          scoreRange: scoreRangeOf(graph, itemSel.node.id),
                          onScoreRangeChange: (r) =>
                            mudarGrafo(setScoreRange(graph, itemSel.node.id, r)),
                        }
                      : {})}
                  />
                  <div className="space-y-3 border-t pt-5">
                    <p className="font-display text-sm font-semibold">Lógica</p>
                    <BlockLogic
                      node={itemSel.node}
                      edges={branchEdges(itemSel.node)}
                      onEdgesChange={(edges) =>
                        mudarGrafo(setBranchEdges(graph, itemSel.node.id, edges))
                      }
                      after={itemSel.after}
                      onAfterChange={(after) => mudarGrafo(setAfter(graph, itemSel.node.id, after))}
                      targets={destinos}
                      nextLabel={idxSel + 1 < itens.length ? rotulo(idxSel + 1) : null}
                      variables={variaveis}
                    />
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={MousePointerClick}
                  title={itens.length === 0 ? "Comece pelo primeiro bloco" : "Escolha um bloco"}
                  description={
                    itens.length === 0
                      ? "Um formulário típico: texto de boas-vindas, nome, WhatsApp, uma pergunta de interesse e “Salvar no CRM”."
                      : "Clique num bloco da lista para editar o texto, as opções e a lógica."
                  }
                />
              )}
            </section>

            {/* Prévia */}
            <aside
              className={cn(
                "min-h-0 bg-muted/20 p-4 lg:border-l",
                painel !== "previa" && "hidden lg:block",
              )}
            >
              <FlowPreview graph={graph} theme={theme} kind={kind} settings={settings} />
            </aside>
          </div>
        </TabsContent>

        {/* ── Tema ───────────────────────────────────────────────────────── */}
        <TabsContent value="tema" className="min-h-0 flex-1 pt-0">
          <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
            <div className="min-h-0 overflow-y-auto px-4 py-5 sm:px-6">
              <div className="mx-auto max-w-2xl">
                <ThemeFields theme={theme} onChange={mudarTema} onUploadMedia={uploadFlowMedia} />
              </div>
            </div>
            <aside className="hidden min-h-0 border-l bg-muted/20 p-4 lg:block">
              <FlowPreview graph={graph} theme={theme} kind={kind} settings={settings} />
            </aside>
          </div>
        </TabsContent>

        {/* ── Ajustes ────────────────────────────────────────────────────── */}
        {/* Com prévia ao lado, como no Tema: a tela de abertura mora aqui. */}
        <TabsContent value="ajustes" className="min-h-0 flex-1 pt-0">
          <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
            <div className="min-h-0 overflow-y-auto px-4 py-5 sm:px-6">
              <div className="mx-auto max-w-2xl">
                <SettingsFields
                  settings={settings}
                  onChange={mudarAjustes}
                  slug={slug}
                  onSlugChange={mudarSlug}
                  slugError={erroSlug}
                  publicUrl={live.publicUrl}
                  published={live.publishedVersion !== null}
                  onUploadMedia={uploadFlowMedia}
                />
              </div>
            </div>
            <aside className="hidden min-h-0 border-l bg-muted/20 p-4 lg:block">
              <FlowPreview graph={graph} theme={theme} kind={kind} settings={settings} />
            </aside>
          </div>
        </TabsContent>

        <TabsContent
          value="compartilhar"
          className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
        >
          <SharePanel
            flowId={flowId}
            publicUrl={live.publicUrl}
            slug={live.slug}
            published={live.publishedVersion !== null}
          />
        </TabsContent>

        <TabsContent value="respostas" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <SessionsPanel flowId={flowId} graph={graph} kind={kind} />
        </TabsContent>

        <TabsContent value="relatorio" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <ReportPanel flowId={flowId} graph={graph} />
        </TabsContent>
      </Tabs>

      <VersionsDialog
        flowId={flowId}
        currentVersion={live.publishedVersion}
        open={versoes}
        onOpenChange={setVersoes}
        beforeRestore={flush}
        onRestored={onReload}
      />
    </div>
  );
}

/** "Salvando…", "Salvo" ou "Não salvo — tentar de novo", ao lado do nome. */
function EstadoSalvo({ estado, onRetry }: { estado: SaveState; onRetry: () => void }) {
  if (estado === "idle") return null;
  if (estado === "error") {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs font-medium text-destructive hover:underline"
      >
        <CloudOff className="size-3.5" />
        <span className="hidden sm:inline">Não salvo — tentar de novo</span>
      </button>
    );
  }
  const salvando = estado === "pending" || estado === "saving";
  return (
    <span
      aria-live="polite"
      className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
    >
      {salvando ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
      <span className="hidden sm:inline">{salvando ? "Salvando…" : "Salvo"}</span>
    </span>
  );
}

function CabecalhoDoBloco({
  node,
  posicao,
  total,
  problemas,
}: {
  node: FlowNode;
  posicao: number;
  total: number;
  problemas: FlowIssueView[];
}) {
  const Icone = BLOCK_ICON[node.type];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icone className="size-4" />
        </span>
        <div className="min-w-0">
          <p className="font-display text-base font-semibold leading-tight">
            {BLOCK_LABEL[node.type]}
          </p>
          <SectionLabel>
            Bloco {posicao} de {total}
          </SectionLabel>
        </div>
      </div>
      {problemas.length > 0 && (
        <ul className="space-y-1">
          {problemas.map((p, i) => (
            <li
              key={i}
              className={cn(
                "flex items-start gap-2 rounded-lg px-3 py-2 text-xs",
                p.level === "error"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-warning/18 text-warning-foreground dark:text-warning",
              )}
            >
              {p.level === "error" ? (
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              )}
              {p.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
